import React, { useState, useCallback } from 'react';
import { generateUnitLessons } from '../../api/classes';
import styles from './LessonGenerationPanel.module.css';

export default function LessonGenerationPanel({ classId, units }) {
  const [generating, setGenerating] = useState({});
  const [generated, setGenerated] = useState({});
  const [errors, setErrors] = useState({});

  const anyGenerating = Object.values(generating).some(Boolean);

  const handleGenerateUnit = useCallback(async (unitId) => {
    setGenerating(prev => ({ ...prev, [unitId]: true }));
    setErrors(prev => ({ ...prev, [unitId]: null }));

    try {
      const result = await generateUnitLessons(classId, unitId);
      const count = result?.lessons?.length || 0;
      setGenerated(prev => ({ ...prev, [unitId]: count }));
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        [unitId]: 'Failed to generate lessons. Please try again.',
      }));
    } finally {
      setGenerating(prev => ({ ...prev, [unitId]: false }));
    }
  }, [classId]);

  const handleGenerateAll = useCallback(async () => {
    for (const unit of units) {
      if (!generated[unit.id]) {
        await handleGenerateUnit(unit.id);
      }
    }
  }, [units, generated, handleGenerateUnit]);

  if (!units || units.length === 0) {
    return (
      <div className={styles.panel}>
        <p className={styles.emptyState}>No units available. Generate a scope & sequence first.</p>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.title}>Lesson Generation</h3>
        <button
          type="button"
          className={styles.generateAllBtn}
          onClick={handleGenerateAll}
          disabled={anyGenerating}
        >
          Generate All Lessons
        </button>
      </div>

      <div className={styles.unitList}>
        {units.map(unit => (
          <div key={unit.id} className={styles.unitRow}>
            <div className={styles.unitInfo}>
              <span className={styles.unitNumber}>Unit {unit.sequenceOrder}</span>
              <span className={styles.unitTitle}>{unit.title}</span>
            </div>

            {generating[unit.id] ? (
              <span className={styles.generatingLabel}>Generating...</span>
            ) : generated[unit.id] != null ? (
              <span className={styles.doneLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {generated[unit.id]} lessons created
              </span>
            ) : (
              <button
                type="button"
                className={styles.generateBtn}
                onClick={() => handleGenerateUnit(unit.id)}
                disabled={anyGenerating}
              >
                Generate Lessons
              </button>
            )}

            {errors[unit.id] && (
              <span className={styles.error}>{errors[unit.id]}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
